/**
 * SystemTelemetryBar
 *
 * Slim horizontal bar (32px) that displays live system telemetry metrics
 * across all pages. Sits directly below the app header, above the
 * AuditorBanner and page content.
 *
 * Metrics: System State, Collectors Connected, Policies Active,
 *          Evidence Generated (24h), Last Evaluation
 *
 * Uses React Query with 30s staleTime; falls back to mock data when
 * the API is unavailable.
 */
import React from 'react';
import { makeStyles, tokens, Caption1 } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SystemState = 'ACTIVE' | 'DEGRADED' | 'INACTIVE';

interface TelemetryData {
  systemState: SystemState;
  collectorsConnected: number;
  policiesActive: number;
  evidenceGenerated24h: number;
  lastEvaluation: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<SystemState, string> = {
  ACTIVE: '#22C55E',
  DEGRADED: '#F59E0B',
  INACTIVE: '#EF4444',
};

const MOCK_TELEMETRY: TelemetryData = {
  systemState: 'ACTIVE',
  collectorsConnected: 7,
  policiesActive: 24,
  evidenceGenerated24h: 13294,
  lastEvaluation: '11s ago',
};

// ---------------------------------------------------------------------------
// Data fetching
// ---------------------------------------------------------------------------

async function fetchTelemetry(): Promise<TelemetryData> {
  // Endpoint does not exist yet — return mock data silently.
  // When the API adds /portal/telemetry/summary, remove this early return.
  return MOCK_TELEMETRY;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  bar: {
    display: 'flex',
    alignItems: 'center',
    height: '32px',
    minHeight: '32px',
    maxHeight: '32px',
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    gap: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: 'hidden',
    flexShrink: 0,
  },
  metric: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    whiteSpace: 'nowrap',
  },
  /** Hide less-critical metrics on narrow viewports */
  hideBelowMd: {
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  hideBelowSm: {
    '@media (max-width: 480px)': {
      display: 'none',
    },
  },
  label: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    userSelect: 'none',
  },
  value: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
  },
  dot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  separator: {
    width: '1px',
    height: '14px',
    backgroundColor: tokens.colorNeutralStroke2,
    flexShrink: 0,
  },
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toLocaleString();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const Separator: React.FC = () => {
  const styles = useStyles();
  return <span className={styles.separator} aria-hidden="true" />;
};

export const SystemTelemetryBar: React.FC = () => {
  const styles = useStyles();

  const { data } = useQuery<TelemetryData>({
    queryKey: ['system-telemetry'],
    queryFn: fetchTelemetry,
    staleTime: 30_000,
    refetchInterval: 30_000,
    retry: 1,
  });

  const telemetry = data ?? MOCK_TELEMETRY;
  const stateColor = STATUS_COLORS[telemetry.systemState] ?? STATUS_COLORS.ACTIVE;

  return (
    <div className={styles.bar} role="status" aria-label="System telemetry">
      {/* System State -- always visible */}
      <div className={styles.metric}>
        <span
          className={`${styles.dot} system-pulse`}
          style={{ backgroundColor: stateColor }}
          aria-hidden="true"
        />
        <Caption1 className={styles.label}>Governance Runtime</Caption1>
        <Caption1
          className={`${styles.value} metric-value`}
          style={{ color: stateColor }}
        >
          {telemetry.systemState ?? '\u2014'}
        </Caption1>
      </div>

      <Separator />

      {/* Collectors Connected -- hidden below 480px */}
      <div className={`${styles.metric} ${styles.hideBelowSm}`}>
        <Caption1 className={styles.label}>Collectors</Caption1>
        <Caption1 className={`${styles.value} metric-value`}>
          {telemetry.collectorsConnected != null
            ? formatNumber(telemetry.collectorsConnected)
            : '\u2014'}
        </Caption1>
      </div>

      <span className={styles.hideBelowSm}>
        <Separator />
      </span>

      {/* Policies Active -- hidden below 480px */}
      <div className={`${styles.metric} ${styles.hideBelowSm}`}>
        <Caption1 className={styles.label}>Policies</Caption1>
        <Caption1 className={`${styles.value} metric-value`}>
          {telemetry.policiesActive != null
            ? formatNumber(telemetry.policiesActive)
            : '\u2014'}
        </Caption1>
      </div>

      <span className={styles.hideBelowMd}>
        <Separator />
      </span>

      {/* Evidence Generated (24h) -- hidden below 768px */}
      <div className={`${styles.metric} ${styles.hideBelowMd}`}>
        <Caption1 className={styles.label}>Evidence (24h)</Caption1>
        <Caption1 className={`${styles.value} metric-value`}>
          {telemetry.evidenceGenerated24h != null
            ? formatNumber(telemetry.evidenceGenerated24h)
            : '\u2014'}
        </Caption1>
      </div>

      <span className={styles.hideBelowMd}>
        <Separator />
      </span>

      {/* Last Evaluation -- hidden below 768px */}
      <div className={`${styles.metric} ${styles.hideBelowMd}`}>
        <Caption1 className={styles.label}>Last Eval</Caption1>
        <Caption1 className={`${styles.value} metric-value`}>
          {telemetry.lastEvaluation ?? '\u2014'}
        </Caption1>
      </div>
    </div>
  );
};
